import {
  ADD_TO_BASKET_ESI_CHILD_ITEMS_UPDATED_POST_A2B,
  ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
} from "@/constants/constants";
import { Drawer } from "@JohnLewisPartnership/jl-design-system/dist/components/drawer/Drawer";
import styles from "./ProtectPlusComplianceDrawer.module.scss";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { TickOutlined as Tick } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickOutlined";
import { DEPRECATED_Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";

import { VariantServices } from "@/types/types.ts";
import {
  Services,
  TransformedExtendedWarrantyMulti,
} from "@/types/serviceTypes.ts";
import { SharedServicesActions } from "@/utils/services/useSharedServices";
import {
  OPEN_POST_ADD_TO_BASKET_DRAWER,
  PRE_ADD_TO_BASKET_DRAWER,
} from "./components/AvailableServices";
import { BasketMode } from "@/components/PostAddToBasketDrawer/basketMode.ts";

import { ProtectPlusScheme } from "@/components/PostAddToBasketDrawer/scheme/schemeDecisioner.ts";
import { ProtectPlusCompliance } from "@/components/PostAddToBasketDrawer/components/ProtectPlusCompliance/ProtectPlusCompliance.tsx";
import {
  addServiceToBasket,
  changeItemQuantity,
  getBasket as getBasketFromApi,
} from "@/utils/basket/basketApi";
import { BasketRequestResponse } from "@/utils/basket/types";
import classNames from "classnames";

type Props = {
  title: string;
  variantServices: VariantServices;
  basketBaseUrl: string;
  productId: string;
  skuId: string;
  guarantees?: { title: string }[];
  basketMode: BasketMode;
  protectPlusScheme: ProtectPlusScheme;
  hideFooter: boolean;
  mandatoryServicesOverride: string[];
  hasWholePoundsOnlyEnabled?: boolean;
  /** Available services — passed down from the container. */
  availableServices: Services;
  /** Store mutation actions — passed down from the container. */
  actions: SharedServicesActions;
};

export const postAddToBasketDrawerEvent = "POST_ADD_TO_BASKET_DRAWER_OPENED";

export const ProtectPlusComplianceDrawerUI = ({
  title,
  variantServices,
  skuId,
  basketMode,
  guarantees = [],
  protectPlusScheme,
  basketBaseUrl,
  hideFooter,
  availableServices = [],
  actions,
  hasWholePoundsOnlyEnabled,
}: Props) => {
  const drawerHeaderDefault = "Protect Plus";
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const commerceItems = useRef<null | Array<any>>(null);
  const basketItemFromApi = useRef<BasketRequestResponse | undefined>(
    undefined,
  );
  const changeQuantityTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const changeQuantityResolve = useRef<((success: boolean) => void) | null>(
    null,
  );
  const [complianceDrawerHeader, setComplianceDrawerHeader] =
    useState<string>(drawerHeaderDefault);
  const [basketQuantity, setBasketQuantity] = useState(0);
  const [apiError, setApiError] = useState(false);
  const [localQuantity, setLocalQuantity] = useState(0);
  const showTitleWithoutTick = hideFooter || basketQuantity === 0;
  const extendedWarrantySkuIds = useMemo(
    () =>
      variantServices
        ?.filter((service) => service.__typename === "ExtendedWarranty")
        .map((service) => service.aliases.skuId) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const pplusServiceBase = availableServices.find(
    (s) => s.serviceType === "ExtendedWarranty",
  );

  const warrantyService: TransformedExtendedWarrantyMulti | undefined =
    pplusServiceBase
      ? { ...pplusServiceBase, quantity: localQuantity }
      : undefined;

  useEffect(() => {
    if (
      warrantyService?.selected &&
      basketMode === BasketMode.Pre &&
      extendedWarrantySkuIds.length > 0
    ) {
      if (pplusServiceBase) {
        actions.removeService(pplusServiceBase.id);
      }
    }
  }, [variantServices, basketMode, extendedWarrantySkuIds, skuId]);

  const openPostAddToBasketDrawer = useCallback(
    async (event: CustomEvent<{ errors: [] }>) => {
      const hasError = event.detail.errors?.length > 0;
      if (hasError || basketMode === BasketMode.Pre) return;

      const apiBasket = await getBasketFromApi(basketBaseUrl);
      const commerceItem = apiBasket.basket?.commerceItems?.find(
        (item) => item.skuId === skuId,
      );
      const pplusChild = commerceItem?.childItems?.find((child) =>
        extendedWarrantySkuIds.includes(child.skuId),
      );

      if (commerceItem && commerceItem.quantity > (pplusChild?.quantity ?? 0)) {
        basketItemFromApi.current = apiBasket;
        setIsDrawerOpen(true);
        dispatchMonetateQEvent(postAddToBasketDrawerEvent);
      }
    },
    [skuId, basketBaseUrl, basketMode, extendedWarrantySkuIds],
  );

  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
    dispatchMonetateQEvent(postAddToBasketDrawerEvent);
  }, []);

  const openPreAddToBasketDrawer = () => {
    setIsDrawerOpen(true);
  };

  // Deselect extended warranty services on successful basket add.
  useEffect(() => {
    const handleBasketSuccess = () => {
      availableServices
        .filter((s) => s.serviceType === "ExtendedWarranty" && !s.isMandatory)
        .forEach((s) => actions.removeService(s.id));
    };

    window.addEventListener(
      ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
      handleBasketSuccess,
    );
    return () => {
      window.removeEventListener(
        ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
        handleBasketSuccess,
      );
    };
  }, [availableServices, actions]);

  useEffect(() => {
    if (basketMode == BasketMode.Pre) {
      window.addEventListener(
        PRE_ADD_TO_BASKET_DRAWER,
        openPreAddToBasketDrawer,
      );
    } else {
      window.addEventListener(OPEN_POST_ADD_TO_BASKET_DRAWER, openDrawer);
    }

    window.addEventListener(
      ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
      openPostAddToBasketDrawer,
    );

    return () => {
      if (basketMode == BasketMode.Pre) {
        window.removeEventListener(
          PRE_ADD_TO_BASKET_DRAWER,
          openPreAddToBasketDrawer,
        );
      } else {
        window.removeEventListener(OPEN_POST_ADD_TO_BASKET_DRAWER, openDrawer);
      }
      window.removeEventListener(
        ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
        openPostAddToBasketDrawer,
      );
    };
  }, [openPostAddToBasketDrawer, openDrawer, skuId]);

  const flushUpdatedCommerceItems = () => {
    if (commerceItems.current !== null) {
      dispatchEvent(
        new CustomEvent(ADD_TO_BASKET_ESI_CHILD_ITEMS_UPDATED_POST_A2B, {
          detail: {
            commerceItems: commerceItems.current,
          },
        }),
      );
      commerceItems.current = null;
    }
  };

  const closeDrawer = () => {
    flushUpdatedCommerceItems();
    setIsDrawerOpen(false);
  };

  // Sync basket state from API when the drawer opens/closes.
  useEffect(() => {
    if (isDrawerOpen) {
      const applyBasket = (apiBasket: BasketRequestResponse) => {
        const commerceItem = apiBasket.basket?.commerceItems?.find(
          (item) => item.skuId === skuId,
        );

        if (commerceItem) {
          setBasketQuantity(commerceItem.quantity);
          setComplianceDrawerHeader("Added to your basket");
          if (pplusServiceBase) {
            const pplusChild = commerceItem.childItems?.find((child) =>
              extendedWarrantySkuIds.includes(child.skuId),
            );
            setLocalQuantity(pplusChild?.quantity ?? 0);
          }
        } else {
          setBasketQuantity(0);
          setComplianceDrawerHeader(drawerHeaderDefault);
          setLocalQuantity(0);
        }
      };

      if (basketItemFromApi.current) {
        applyBasket(basketItemFromApi.current);
      } else {
        getBasketFromApi(basketBaseUrl).then((apiBasket) => {
          basketItemFromApi.current = apiBasket;
          applyBasket(apiBasket);
        });
      }
    } else {
      basketItemFromApi.current = undefined;
      if (changeQuantityTimer.current) {
        clearTimeout(changeQuantityTimer.current);
        changeQuantityTimer.current = null;
        changeQuantityResolve.current?.(false);
        changeQuantityResolve.current = null;
      }
    }
  }, [isDrawerOpen]);

  function setServiceSelected(serviceId: string, selected = true) {
    if (selected) {
      actions.selectService(serviceId);
    } else {
      actions.removeService(serviceId);
    }
  }

  const handleChangeQuantity = (
    service: TransformedExtendedWarrantyMulti,
    quantity: number,
  ): Promise<boolean> => {
    if (changeQuantityTimer.current) {
      clearTimeout(changeQuantityTimer.current);
      changeQuantityResolve.current?.(false);
    }

    return new Promise((resolve) => {
      changeQuantityResolve.current = resolve;

      changeQuantityTimer.current = setTimeout(async () => {
        changeQuantityTimer.current = null;
        changeQuantityResolve.current = null;

        const serviceItemId = basketItemFromApi.current?.basket?.commerceItems
          ?.find((item) => item.skuId === skuId)
          ?.childItems?.find((child) => child.skuId === service.skuId)?.id;

        if (!serviceItemId) {
          resolve(false);
          return;
        }

        setApiError(false);

        const response = await changeItemQuantity(
          basketBaseUrl,
          serviceItemId,
          quantity,
        );

        if (response.success) {
          commerceItems.current = response.commerceItems;
          setLocalQuantity(quantity);
        } else {
          setApiError(true);
        }

        resolve(response.success);
      }, 300);
    });
  };

  const handleToggleSelectedServiceClick = async (
    service: TransformedExtendedWarrantyMulti,
  ) => {
    setServiceSelected(service.id, true);
  };

  const handleAddProtectPlusClick = async (
    service: TransformedExtendedWarrantyMulti,
    quantity: number,
  ) => {
    const parentItemId = basketItemFromApi.current?.basket?.commerceItems?.find(
      (item) => item.skuId === skuId,
    )?.id;

    if (!parentItemId) {
      setApiError(true);
      return;
    }

    setApiError(false);

    const result = await addServiceToBasket(
      basketBaseUrl,
      parentItemId,
      service.id,
      quantity,
    );

    if (result.success) {
      commerceItems.current = result.commerceItems;
      setLocalQuantity(quantity);
      window.dispatchEvent(
        new CustomEvent("jl-events.browser.basketQuantityUpdated"),
      );
    } else {
      setApiError(true);
    }
  };

  if (!warrantyService) return null;

  return (
    <Drawer
      isOpen={isDrawerOpen}
      onClose={closeDrawer}
      heading={complianceDrawerHeader}
      className={styles.drawer}
      primaryButtonText="Continue shopping"
      onPrimaryButtonClick={closeDrawer}
      underlinedButtonText={""}
    >
      <div
        className={classNames(
          styles.drawer__titleItem,
          showTitleWithoutTick ? styles.drawer__titleItem_no_tick : "",
        )}
        data-testid={"post-a2b-drawer-product-title"}
      >
        {!showTitleWithoutTick && <Tick size="medium" />}
        <div>
          {basketQuantity > 0 && (
            <>
              <span> {basketQuantity} </span>
              <span className={styles.timesSymbol} aria-label="times">
                X
              </span>
            </>
          )}
          <span> {title} </span>
        </div>
      </div>
      {apiError && (
        <DEPRECATED_Message
          body="Sorry, something went wrong. Please try again later"
          type="error"
          className={styles.drawer__message}
        />
      )}
      <ProtectPlusCompliance
        hasWholePoundsOnlyEnabled={hasWholePoundsOnlyEnabled}
        mode={basketMode}
        protectPlusScheme={protectPlusScheme}
        guarantees={guarantees}
        warrantyService={warrantyService}
        addProtectPlusClicked={async (service, serviceQuantity) => {
          if (service.quantity === serviceQuantity) {
            return true;
          }

          if (basketQuantity > 0) {
            await handleAddProtectPlusClick(service, serviceQuantity);
          } else {
            await handleToggleSelectedServiceClick(service);
          }

          return !apiError;
        }}
        closeDrawer={closeDrawer}
        maxAllowedServices={basketQuantity}
        handleStepperChange={handleChangeQuantity}
        hideFooter={hideFooter}
        isDrawerOpen={isDrawerOpen}
      />
    </Drawer>
  );
};
